import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { AuthService } from 'app/um/services/auth-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { EventAggregator } from 'aurelia-event-aggregator';
import { DiagnosisFlagReloadEvent } from 'app/um/lib/events';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveDiagnosisFlag } from 'app/um/components/diagnosis-flags/save-diagnosis-flag';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class DiagnosisFlags extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected diagnosisFlags: UM.AuthFlag[] = [];
    protected editedDiagnosisFlag: UM.AuthFlag | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    protected $saveDiagnosisFlagModal!: IPopup;
    protected $form!: IForm;
    protected $diagnosisFlagForm!: Element;
    protected $saveDiagnosisFlag!: SaveDiagnosisFlag;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private service: AuthService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected async formSuccess(): Promise<void> {
        this.saveDiagnosisFlagClose();
        await this.reload();
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadDiagnosisFlags();
    }

    protected saveDiagnosisFlag(diagnosisFlag: UM.AuthFlag | null): void {
        this.editedDiagnosisFlag = diagnosisFlag;
        this.$saveDiagnosisFlagModal.open();
    }

    protected saveDiagnosisFlagClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveDiagnosisFlagModal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadDiagnosisFlags(): Promise<void> {
        const includes: string[] = [];

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthFlags(options, includes, 'diagnoses');
            this.diagnosisFlags = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateDiagnosisFlag(diagnosisFlag: UM.AuthFlag): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(diagnosisFlag.isActive) {
            const result = await confirmation.showDeactivate('um:auth.flag.diagnosisFlag.$', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAuthFlag(diagnosisFlag.id!, !diagnosisFlag.isActive);
            isSuccessful = response.isSuccessful;
            this.events.publish(new DiagnosisFlagReloadEvent());
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('um:auth.flag.diagnosisFlag.$', !diagnosisFlag.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
